// Экран заметки на телефоне: читалка, шторка агента, диктовка (M4).
//
// Главное решение экрана — тела заметки здесь не правят руками (M4.7).
// Никакого contentEditable: это не недоделка, а граница. Справка на 200 строк,
// переписанная с экранной клавиатуры в трясущемся вагоне, — испорченная справка;
// а перенести сюда десктопную правку по блокам мешает ещё и то, что каретка в
// contentEditable на iOS живёт своей жизнью. Поэтому текст меняется двумя
// способами: голосом и просьбой агенту, и оба уходят предложением в ленту
// (M5.3, M9.3), а не пишут в файл.
//
// Разбор markdown переиспользуем из десктопного хаба (`hub/notes.ts`): второй
// парсер означал бы, что одна и та же таблица рисуется на телефоне не так, как
// на десктопе. Отсюда же индекс wiki-ссылок.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, notesApi } from '../api'
import {
  buildLinkIndex, normalizeName, parseDoc, parseInline, resolveWiki,
  type Block, type Inline,
} from '../hub/notes'
import type { NoteContext, NoteFull, NoteType } from '../protocol'
import { useAppStore } from '../store/appStore'
import AgentSheet from './AgentSheet'
import DictationSheet from './DictationSheet'
import Sheet from './Sheet'
import './note.css'

const TYPE_LABEL: Record<NoteType, string> = {
  note: 'заметка',
  goal: 'цель',
  project: 'проект',
  reference: 'справка',
  log: 'лог',
  message: 'сообщение',
  task: 'задача',
}

const TOAST_MS = 3600

function updatedLabel(ts: number): string {
  const at = new Date(ts * 1000)
  const now = new Date()
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  const time = at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (sameDay) return `правка сегодня в ${time}`
  return `правка ${at.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} в ${time}`
}

/** Как разрешать `[[имя]]`: путь заметки либо признак «ещё не написана». */
type Resolve = (name: string) => { path: string | null; missing: boolean }

interface InlineProps {
  parts: Inline[]
  resolve: Resolve
  onOpen(path: string): void
  onToast(text: string): void
}

/** Куски строки. Внутренняя ссылка — подчёркнутый текст, а не кнопка: в потоке
 *  чтения кнопка ломает строку, а подчёркивание читается как ссылка (M4.4). */
function Inlines({ parts, resolve, onOpen, onToast }: InlineProps) {
  return (
    <>
      {parts.map((part, i) => {
        if (part.kind === 'text') return <Fragment key={i}>{part.text}</Fragment>
        if (part.kind === 'strong') return <strong key={i}>{part.text}</strong>
        if (part.kind === 'em') return <em key={i}>{part.text}</em>
        if (part.kind === 'code') {
          return <code key={i} className="ab-mnote__icode">{part.text}</code>
        }
        if (part.kind === 'link') {
          return (
            <a key={i} className="ab-mnote__link" href={part.href} target="_blank" rel="noreferrer">
              {part.text}
            </a>
          )
        }
        const target = resolve(part.name)
        if (target.path !== null) {
          const to = target.path
          return (
            <span
              key={i}
              className="ab-mnote__wiki"
              role="link"
              tabIndex={0}
              onClick={() => onOpen(to)}
            >
              {part.label}
            </span>
          )
        }
        // Ненаписанная заметка не открывается: показываем тост, а не пустой
        // экран «заметка не найдена» — переход в никуда хуже отказа (M4.4).
        return (
          <span
            key={i}
            className="ab-mnote__wiki is-missing"
            role="link"
            tabIndex={0}
            onClick={() =>
              onToast(
                target.missing
                  ? `Заметки «${part.name}» ещё нет — попросите агента её написать.`
                  : `Не удалось определить путь заметки «${part.name}».`,
              )
            }
          >
            {part.label}
          </span>
        )
      })}
    </>
  )
}

interface BlockProps {
  block: Block
  resolve: Resolve
  onOpen(path: string): void
  onToast(text: string): void
}

/** Блоки читалки (M4.3). Правка на месте намеренно отсутствует. */
function BlockView({ block, resolve, onOpen, onToast }: BlockProps) {
  const inline = (text: string) => (
    <Inlines parts={parseInline(text)} resolve={resolve} onOpen={onOpen} onToast={onToast} />
  )

  switch (block.kind) {
    case 'heading':
      // Глубже второго уровня заголовки на 390 px уже не различаются размером,
      // поэтому вся мелочь сводится к одному «малому» заголовку.
      return block.level <= 2
        ? <h2 className="ab-mnote__h2">{inline(block.text)}</h2>
        : <h3 className="ab-mnote__h3">{inline(block.text)}</h3>
    case 'paragraph':
      return <p className="ab-mnote__p">{inline(block.text)}</p>
    case 'list':
      return block.ordered ? (
        <ol className="ab-mnote__list" start={block.start}>
          {block.items.map((item, i) => <li key={i}>{inline(item)}</li>)}
        </ol>
      ) : (
        <ul className="ab-mnote__list">
          {block.items.map((item, i) => <li key={i}>{inline(item)}</li>)}
        </ul>
      )
    case 'quote':
      return (
        <blockquote className="ab-mnote__quote">
          {block.lines.map((line, i) => <p key={i}>{inline(line)}</p>)}
        </blockquote>
      )
    case 'code':
      // Код не переносим: сломанный по случайному месту отступ читается хуже,
      // чем горизонтальный скролл (M4.3).
      return <pre className="ab-mnote__code"><code>{block.text}</code></pre>
    case 'table':
      return (
        <div className="ab-mnote__tablewrap">
          <table className="ab-mnote__table">
            <thead>
              <tr>{block.head.map((cell, i) => <th key={i}>{inline(cell)}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>{row.map((cell, j) => <td key={j}>{inline(cell)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'rule':
      return <hr className="ab-mnote__rule" />
  }
}

interface Props {
  path: string
  onOpen(path: string): void
}

export default function NoteScreen({ path, onOpen }: Props) {
  const tree = useAppStore((s) => s.tree)
  const serif = useAppStore((s) => s.serif)
  const toggleSerif = useAppStore((s) => s.toggleSerif)
  // Диктовке нужна сессия, в которую уедет расшифровка; своей у экрана нет.
  const sessionId = useAppStore((s) => s.activeId)

  const [note, setNote] = useState<NoteFull | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [askOpen, setAskOpen] = useState(false)
  const [recOpen, setRecOpen] = useState(false)
  const [ctxOpen, setCtxOpen] = useState(false)
  // Контекст храним вместе с путём, для которого его запросили: экран у всех
  // заметок один и тот же (MobileApp меняет проп `path`, а не пересоздаёт
  // инстанс), поэтому ответ, пришедший после перехода по wiki-ссылке, обязан
  // быть узнан как чужой — иначе в заметке B висел бы контекст заметки A.
  const [ctx, setCtx] = useState<{ path: string; data: NoteContext } | null>(null)
  /** путь заметки, чей контекст сейчас в полёте; null — запроса нет */
  const ctxReq = useRef<string | null>(null)

  useEffect(() => {
    let alive = true
    setNote(null)
    setError(null)
    setCtx(null)
    setCtxOpen(false)
    ctxReq.current = null
    notesApi
      .get(path)
      .then((full) => { if (alive) setNote(full) })
      .catch((cause) => {
        if (!alive) return
        setError(cause instanceof ApiError ? cause.message : 'Не удалось открыть заметку')
      })
    return () => { alive = false }
  }, [path])

  useEffect(() => {
    if (toast === null) return
    const timer = window.setTimeout(() => setToast(null), TOAST_MS)
    return () => window.clearTimeout(timer)
  }, [toast])

  const doc = useMemo(() => parseDoc(note?.body ?? ''), [note?.body])

  // Индекс имён строим и по дереву, и по путям, которые сервер уже разрешил:
  // дерево знает заголовки, `links` — реальные пути (см. hub/notes.ts).
  const resolve = useMemo<Resolve>(() => {
    const index = buildLinkIndex(tree, note?.links ?? [])
    const missing = new Set((note?.missing ?? []).map(normalizeName))
    return (name: string) => resolveWiki(name, index, missing)
  }, [tree, note?.links, note?.missing])

  const openContext = useCallback(() => {
    setCtxOpen(true)
    if (ctx !== null && ctx.path === path) return // уже прочитан для этой заметки
    if (ctxReq.current === path) return // уже в полёте
    ctxReq.current = path
    notesApi
      .context(path)
      .then((data) => {
        if (ctxReq.current !== path) return // ушли на другую заметку — ответ чужой
        ctxReq.current = null
        setCtx({ path, data })
      })
      .catch(() => {
        if (ctxReq.current !== path) return
        ctxReq.current = null
        setToast('Контекст заметки не удалось загрузить')
      })
  }, [ctx, path])

  // Расшифровка не пишется в файл (M9.3): она уходит агенту, а правка
  // возвращается предложением в ленту. Открываем шторку агента, чтобы человек
  // видел, куда именно ушла реплика.
  const onDictated = useCallback((text: string) => {
    setRecOpen(false)
    setAskOpen(true)
    setToast(text.trim() ? 'Надиктовано — реплика ушла агенту, правка придёт в ленту.' : 'Ничего не расслышал.')
  }, [])

  // Ответ на прошлую заметку не показываем даже мельком: пока не пришёл свой,
  // шторка честно пишет «Читаю контекст…».
  const ctxData = ctx !== null && ctx.path === path ? ctx.data : null

  if (error !== null) {
    return <div className="ab-mnote ab-mnote--flat"><div className="ab-mnote__error">{error}</div></div>
  }

  if (note === null) {
    return <div className="ab-mnote ab-mnote--flat"><div className="ab-mnote__loading">Открываю заметку…</div></div>
  }

  return (
    <div className="ab-mnote">
      <div className="ab-mscroll ab-mnote__scroll">
        {/* Подшапка: тип, время правки, «Aa» и «Контекст» (M4.2). */}
        <div className="ab-mnote__brow">
          <span className={`ab-mnote__type is-${note.type}`}>{TYPE_LABEL[note.type]}</span>
          <span className="ab-mnote__updated">{updatedLabel(note.updated)}</span>
          <button
            type="button"
            className={`ab-mnote__browbtn ab-mnote__aa${serif ? ' is-on' : ''}`}
            onClick={toggleSerif}
            title={serif ? 'читать гротеском' : 'читать антиквой'}
          >
            Aa
          </button>
          <button type="button" className="ab-mnote__browbtn" onClick={openContext}>
            Контекст
          </button>
        </div>

        <div className="ab-mnote__col">
          <h1 className="ab-mnote__title">{note.title}</h1>

          <article className="ab-mnote__doc">
            {doc.blocks.map((block, i) => (
              <BlockView
                key={i}
                block={block}
                resolve={resolve}
                onOpen={onOpen}
                onToast={setToast}
              />
            ))}
            {doc.blocks.length === 0 && (
              // Подсказка новой заметки (M3.4): создалась и открылась пустой —
              // говорим, чем её наполнить, раз клавиатуры здесь нет.
              <p className="ab-mnote__hint">
                Пусто. Надиктуйте первую мысль кнопкой записи внизу — расшифровка
                уйдёт агенту, а он положит её в заметку.
              </p>
            )}
          </article>

          {/* Ссылаются (M4.6). */}
          <section className="ab-mnote__backs">
            <div className="ab-mnote__backshead">Ссылаются</div>
            {note.backlinks.map((back) => (
              <button
                type="button"
                className="ab-mnote__back"
                key={back.path}
                onClick={() => onOpen(back.path)}
              >
                <span className="ab-mnote__backglyph">↩</span>
                <span className="ab-mnote__backtitle">{back.title}</span>
              </button>
            ))}
            {note.backlinks.length === 0 && (
              <div className="ab-mnote__backsempty">Пока никто не ссылается.</div>
            )}
          </section>
        </div>
      </div>

      {/* Закреплённая панель действий (M4.5). */}
      <div className="ab-mnote__bar">
        <button type="button" className="ab-mnote__ask" onClick={() => setAskOpen(true)}>
          <span className="ab-mnote__askglyph">❯</span>
          <span className="ab-mnote__asktext">спросить агента о заметке</span>
        </button>
        <button
          type="button"
          className="ab-mnote__rec"
          onClick={() => setRecOpen(true)}
          title="надиктовать правку"
        >
          <span className="ab-mnote__recdot" />
        </button>
      </div>

      <AgentSheet path={path} open={askOpen} onClose={() => setAskOpen(false)} />
      <DictationSheet
        open={recOpen}
        target="note"
        sessionId={sessionId}
        onClose={() => setRecOpen(false)}
        onText={onDictated}
      />
      <Sheet open={ctxOpen} title="Контекст" onClose={() => setCtxOpen(false)}>
        <div className="ab-mnote__ctx">
          {ctxData === null && <p className="ab-mnote__ctxempty">Читаю контекст…</p>}
          {ctxData !== null && !ctxData.decided.trim() && !ctxData.open.trim() && (
            <p className="ab-mnote__ctxempty">Выжимки по этой заметке пока нет.</p>
          )}
          {ctxData !== null && ctxData.decided.trim() !== '' && (
            <>
              <div className="ab-mnote__ctxlabel">Решено</div>
              <p className="ab-mnote__ctxtext">{ctxData.decided}</p>
            </>
          )}
          {ctxData !== null && ctxData.open.trim() !== '' && (
            <>
              <div className="ab-mnote__ctxlabel">Открытые вопросы</div>
              <p className="ab-mnote__ctxtext">{ctxData.open}</p>
            </>
          )}
        </div>
      </Sheet>

      {toast !== null && <div className="ab-mnote__toast">{toast}</div>}
    </div>
  )
}
